import React, { useCallback, useMemo } from "react";

import { addMinutes, formatISO, subMinutes } from "date-fns";
import { uniqBy } from "lodash-es";
import { useSessionStorage } from "usehooks-ts";

import { useGetNodeExecutions } from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { ErroredRow } from "src/api/types";
import { FixedPositionedDropdownElement } from "src/base-components/FixedPositionedDropDown";
import { DecisionEnvironment } from "src/clients/history_v3";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { useSelectedDatasetId } from "src/datasets/store/useSelectedDatasetId";
import {
  getIntermediateResultColumns,
  StatusColumnProps,
  StatusFilterKey,
} from "src/dataTable/TableUtils";
import { ResultDataAndAuxRowV2 } from "src/dataTable/types";
import { Callout } from "src/design-system/Callout";
import { useEntitySchemas } from "src/entities/queries";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { JobRun } from "src/jobs/types";
import { IntermediateResultsTableV2 } from "src/nodeEditor/IntermediateResultsTableV2";
import { RowCountInfo } from "src/nodeEditor/RowCountInfo";
import { useJobRunNodeStatusCounts } from "src/nodeEditor/useJobRunNodeStatusCounts";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { useSetEditDatasetId } from "src/router/SearchParams";
import { useGraphStore } from "src/store/StoreProvider";

type Props = {
  jobRun: JobRun;
  showErrorInEditor: (error: ErroredRow) => void;
  openEditor: () => void;
  fieldNameToSourceNode?: Map<string, { id: string; type: NODE_TYPE }>;
};

export const JobRunIntermediateResultTableWrapper: React.FC<Props> = ({
  jobRun,
  fieldNameToSourceNode,
}) => {
  const { workspace, flow } = useAuthoringContext();
  const featureGates = useWorkspaceFeatureGates();
  const { selectedDatasetId } = useSelectedDatasetId();
  const { lastRunDatasetId, selectedNode, nodes } = useGraphStore();
  const [environment] = useEnvironment();

  const getNodeName = useCallback(
    (nodeId: string) => nodes.get(nodeId)?.data.label,
    [nodes],
  );

  const [statusFilter, setStatusFilter] = useSessionStorage<StatusFilterKey>(
    `status-filter-${jobRun.id}-${selectedNode?.id}`,
    "all",
  );

  const {
    isJobRunning,
    isOutputNode,
    successCount,
    failureCount,
    ignoredCount,
  } = useJobRunNodeStatusCounts(jobRun);

  const statusElements: FixedPositionedDropdownElement<
    string,
    StatusFilterKey
  >[] = useMemo(
    () => [
      { key: "all", value: "All" },
      {
        key: "success",
        value: "Successful",
        disabled: successCount === 0,
      },
      {
        key: "failure",
        value: "Failed",
        disabled: failureCount === 0,
      },
      {
        key: "ignored",
        value: "Ignored",
        disabled: ignoredCount === 0,
      },
    ],
    [successCount, failureCount, ignoredCount],
  );

  const statusFilterProps: StatusColumnProps = useMemo(
    () => ({
      isFiltering: statusFilter !== "all",
      onSelect: (key) => setStatusFilter(key),
      selected: statusFilter,
      elements: statusElements,
    }),
    [statusFilter, statusElements, setStatusFilter],
  );

  const { data: entitySchemas } = useEntitySchemas({
    baseUrl: workspace.base_url ?? "",
    options: {
      enabled: featureGates.entitiesEnabled,
    },
  });

  const timeFilter = useMemo(() => {
    return {
      fromStartTime: formatISO(subMinutes(jobRun.created_at, 60)),
      untilStartTime: formatISO(addMinutes(jobRun.updated_at, 60)),
    };
  }, [jobRun.created_at, jobRun.updated_at]);

  const {
    data: nodeResults,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isPolling,
  } = useGetNodeExecutions({
    baseUrl: workspace.base_url ?? "",
    nodeId: selectedNode?.id ?? "",
    jobRunId: jobRun.id,
    flowId: flow.id,
    environment: DecisionEnvironment.TEST,
    ...timeFilter,
    afterDecisionId: "",
    beforeDecisionId: "",
    pageSize: 100,
    expectedResultsCount: jobRun.total_rows_count,
  });

  const { data: eventSchemas } = useEventSchemas();
  const columns = useMemo(() => {
    const dataColumns = Object.keys(
      nodeResults?.pages[0]?.results[0]?.data ?? {},
    );

    return getIntermediateResultColumns(
      dataColumns,
      [],
      [],
      statusFilterProps,
      fieldNameToSourceNode,
      entitySchemas?.entities ?? [],
      eventSchemas ?? [],
      null,
      featureGates,
      environment,
    );
  }, [
    nodeResults,
    statusFilterProps,
    fieldNameToSourceNode,
    entitySchemas?.entities,
    eventSchemas,
    featureGates,
    environment,
  ]);

  const dataToDisplay: ResultDataAndAuxRowV2[] = useMemo(() => {
    const results: ResultDataAndAuxRowV2[] = [];
    const deduplicatedResults = uniqBy(
      nodeResults?.pages.flatMap((page) => page.results) ?? [],
      "decision_metadata.id",
    );

    deduplicatedResults.forEach((result, index) => {
      if (result.decision_metadata.status_code === "200") {
        results.push({
          index,
          type: "success",
          data: result.data,
          auxData: {},
          decisionId: result.decision_metadata.id,
          nodeExecutionMetadata: result.execution_metadata,
        });
        return;
      }

      if (result.decision_metadata.error) {
        results.push({
          index,
          type: "failure",
          auxData: {},
          data: result.data,
          errorMessage: result.decision_metadata.error,
          errorOriginNodeId: result.node_id,
          nodeExecutionMetadata: result.execution_metadata,
          decisionId: result.decision_metadata.id,
        });
        return;
      }

      results.push({
        index,
        type: "ignored",
        data: result.data,
        auxData: {},
        decisionId: result.decision_metadata.id,
        nodeExecutionMetadata: result.execution_metadata,
      });
    });

    return results;
  }, [nodeResults]);

  const setEditDatasetId = useSetEditDatasetId();
  const onIgnoredTooltipClick = useCallback(() => {
    if (selectedDatasetId) {
      setEditDatasetId(selectedDatasetId);
    }
  }, [selectedDatasetId, setEditDatasetId]);

  return (
    <IntermediateResultsTableV2
      canFetchNextBatch={!isFetchingNextPage && !isFetching && hasNextPage}
      columns={columns}
      data={dataToDisplay}
      displayingOutputNode={selectedNode?.type === NODE_TYPE.OUTPUT_NODE}
      fetchNextBatch={fetchNextPage}
      getNodeName={getNodeName}
      isFetching={isFetching || isPolling}
      lastRunDatasetID={lastRunDatasetId ?? undefined}
      nodeId={selectedNode?.id ?? ""}
      rowCountInfo={
        <>
          <RowCountInfo
            activeSuccessFilter="success"
            currentFilter={statusFilterProps.selected}
            failureCount={failureCount}
            ignoredCount={ignoredCount}
            successCount={successCount}
            onFilterChange={statusFilterProps.onSelect}
            showWaiting={isOutputNode && isJobRunning}
          />
          {(isJobRunning || isPolling) && (
            <div className="mt-3">
              <Callout type="neutral">
                <p>
                  Test run is still in progress. These results will continue to
                  change during the process.
                </p>
              </Callout>
            </div>
          )}
        </>
      }
      statusFilter={statusFilter}
      testRunId={jobRun.id}
      onErrorClick={() => {}}
      onIgnoredClick={() => {}}
      onIgnoredTooltipClick={onIgnoredTooltipClick}
    />
  );
};
